import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { z } from "zod";

import { apiClient } from "../../utils/apiClient";
import { parseAndValidateDate } from "../../utils/date";
import { env } from "../../utils/env";
import * as persistentStorage from "../../utils/persistentStorage";

// --- Main Interfaces ---
interface UnifiedUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
}

interface UnifiedOrganization {
  id: string;
  name: string;
  slug: string | null;
  imageUrl: string | null;
}

interface AuthState {
  user: UnifiedUser | null;
  organization: UnifiedOrganization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  jwt: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
}

interface UnifiedAuthContextType
  extends Omit<AuthState, "refreshToken" | "expiresAt"> {
  isReady: boolean;
  authenticate: (payload: {
    code?: string;
    clerk_token?: string;
  }) => Promise<void>;
  clearCoreAuthState: () => void;
  refreshToken: () => Promise<void>;
}

// --- Zod Schemas for API Validation ---
const AuthApiResponseSchema = z.object({
  jwt: z.string(),
  refreshToken: z.string(),
  expiresAt: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    imageUrl: z.string().nullable(),
  }),
  organization: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string().nullable(),
    imageUrl: z.string().nullable(),
  }),
});

const RefreshTokenResponseSchema = z.object({
  jwt: z.string(),
  refreshToken: z.string(),
  expiresAt: z.string(),
});

// --- Context Definition ---
const UnifiedAuthContext = createContext<UnifiedAuthContextType | null>(null);

// --- Provider Component ---
interface UnifiedAuthProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = "frontier_unified_auth_state";

const getApiBaseUrl = () => {
  const apiUrl = env.API_URL;
  if (!apiUrl) {
    throw new Error("API_URL is required for authentication");
  }
  return apiUrl;
};

export function UnifiedAuthProvider({
  children,
}: Readonly<UnifiedAuthProviderProps>) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    organization: null,
    isAuthenticated: false,
    isLoading: true,
    jwt: null,
    refreshToken: null,
    expiresAt: null,
  });
  const [isReady, setIsReady] = useState(false);

  const refreshAndSetAuthState = useCallback(
    async (refreshTokenValue: string) => {
      const response = await fetch(
        `${getApiBaseUrl()}/api/auth/refresh-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
        },
      );

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      const validatedData = RefreshTokenResponseSchema.parse(data);

      setAuthState((prevState) => {
        const newState = {
          ...prevState,
          isAuthenticated: true,
          isLoading: false,
          jwt: validatedData.jwt,
          refreshToken: validatedData.refreshToken,
          expiresAt: (() => {
            const expiresAt = parseAndValidateDate(validatedData.expiresAt);
            return expiresAt ? expiresAt.getTime() : Date.now();
          })(),
        };
        persistentStorage
          .setItem(STORAGE_KEY, JSON.stringify(newState))
          .catch((error) => console.error("Failed to save auth state:", error));
        return newState;
      });
    },
    [],
  );

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedState = await persistentStorage.getItem(STORAGE_KEY);
        if (storedState) {
          console.log("Found stored auth state, initializing...");
          const parsedState = JSON.parse(storedState) as AuthState;
          if (parsedState.expiresAt && parsedState.expiresAt > Date.now()) {
            setAuthState({ ...parsedState, isLoading: false });
            console.log("Auth state restored from valid stored state.");
          } else if (parsedState.refreshToken) {
            console.log("Stored session expired, refreshing token...");
            await refreshAndSetAuthState(parsedState.refreshToken);
            console.log("Token refreshed successfully.");
          } else {
            throw new Error("Session expired, no refresh token found");
          }
        } else {
          // No stored state - fresh app start
          console.log("No stored auth state, starting fresh.");
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
            isAuthenticated: false,
          }));
        }
      } catch (error) {
        console.error(
          "Failed to initialize auth state, clearing state.",
          error,
        );
        persistentStorage
          .removeItem(STORAGE_KEY)
          .catch((removeError) =>
            console.error("Failed to clear auth state:", removeError),
          );
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: false,
        }));
      }
    };

    initializeAuth();
  }, [refreshAndSetAuthState]);

  // Configure API client with JWT token whenever auth state changes
  useEffect(() => {
    console.log(
      "Auth state changed. JWT:",
      authState.jwt ? "present" : "absent",
    );
    apiClient.setJwt(authState.jwt);
    if (authState.jwt) {
      console.log("JWT is present, setting isReady to true.");
      setIsReady(true);
    } else {
      console.log("JWT is absent, setting isReady to false.");
      setIsReady(false);
    }
  }, [authState.jwt]);

  const clearCoreAuthState = useCallback(() => {
    persistentStorage
      .removeItem(STORAGE_KEY)
      .catch((error) => console.error("Failed to clear auth state:", error));
    setAuthState({
      user: null,
      organization: null,
      isAuthenticated: false,
      isLoading: false,
      jwt: null,
      refreshToken: null,
      expiresAt: null,
    });
    // The reload is now handled by the caller, as it's context-dependent
    console.log("Core auth state cleared.");
  }, []);

  const authenticate = useCallback(
    async (payload: { code?: string; clerk_token?: string }) => {
      // Use the same endpoint for both desktop codes and Clerk tokens
      const endpoint = "/api/auth/authenticate";
      console.log("Authenticating with payload:", payload);

      const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      console.log("Authentication response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Authentication failed:", errorData);
        throw new Error(
          errorData.error ?? errorData.message ?? "Authentication failed",
        );
      }

      const rawData = await response.text();
      console.log("Authentication response data:", rawData);

      let data: unknown;
      try {
        data = JSON.parse(rawData);
      } catch (_parseError) {
        throw new Error("Invalid response format from authentication service");
      }

      const validatedData = AuthApiResponseSchema.parse(data);

      const newAuthState: AuthState = {
        user: validatedData.user,
        organization: validatedData.organization,
        isAuthenticated: true,
        isLoading: false,
        jwt: validatedData.jwt,
        refreshToken: validatedData.refreshToken,
        expiresAt: (() => {
          const expiresAt = parseAndValidateDate(validatedData.expiresAt);
          return expiresAt ? expiresAt.getTime() : Date.now();
        })(),
      };

      setAuthState(newAuthState);
      persistentStorage
        .setItem(STORAGE_KEY, JSON.stringify(newAuthState))
        .catch((error) => console.error("Failed to save auth state:", error));
    },
    [],
  );

  const value = useMemo(
    () => ({
      ...authState,
      isReady,
      authenticate,
      clearCoreAuthState,
      refreshToken: () => {
        if (!authState.refreshToken) {
          return Promise.reject(new Error("No refresh token available"));
        }
        return refreshAndSetAuthState(authState.refreshToken);
      },
    }),
    [
      authState,
      isReady,
      authenticate,
      clearCoreAuthState,
      refreshAndSetAuthState,
    ],
  );

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

// --- Hooks for consuming context ---
export function useAuth(): UnifiedAuthContextType {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a UnifiedAuthProvider");
  }
  return context;
}

export function useUser() {
  const { user, isLoading } = useAuth();
  return { user, isLoaded: !isLoading };
}

export function useOrganization() {
  const { organization, isLoading } = useAuth();
  return { organization, isLoaded: !isLoading };
}
